// Game constants. Anything tweakable lives here.

import type { Cell, CellNonSpectator, Direction } from './types.js'

export const TICK_INTERVAL_MS = 1000

export const STAKE_PER_ENTRY = 5.0
export const LIQUIDATION_FLOOR = 0.1

// PLAN.md §3.2 step 7 — death-watch hysteresis band
export const DEATH_WATCH_ENTER = 1.0
export const DEATH_WATCH_EXIT = 1.5

// PLAN.md §3.9 — feed staleness thresholds
export const FEED_STALE_MS = 5_000
export const FEED_STALE_60S_MS = 60_000

// PLAN.md §3.4 — tombstone lifetime
export const TOMBSTONE_LIFETIME_TICKS = 30

// PLAN.md §3.3 — move rate-limit per connection
export const MOVE_RATE_LIMIT_MS = 200

// PLAN.md §6.4, §7.9 — price history ring buffer (4h × 3600s)
export const PRICE_RING_CAPACITY = 14_400
export const PRICE_HISTORY_WELCOME_POINTS = 900 // 15 min

// PLAN.md §3.1 — bonus bankroll for new players
export const WELCOME_BONUS = 25.0

// PLAN.md §2.1 — cell semantics
export type CellSpec = {
  cell: CellNonSpectator
  direction: Direction
  display_lev: number
  engine_lev: number
}

// Display label = engine multiplier. Liquidation distances per PLAN.md §4.4
// are unchanged: x50 → ±2%, x25 → ±4%, x10 → ±10%.
export const CELL_SPECS: ReadonlyArray<CellSpec> = [
  { cell: 'short_x50', direction: 'short', display_lev: 50, engine_lev: 50 },
  { cell: 'short_x25', direction: 'short', display_lev: 25, engine_lev: 25 },
  { cell: 'short_x10', direction: 'short', display_lev: 10, engine_lev: 10 },
  { cell: 'long_x10',  direction: 'long',  display_lev: 10, engine_lev: 10 },
  { cell: 'long_x25',  direction: 'long',  display_lev: 25, engine_lev: 25 },
  { cell: 'long_x50',  direction: 'long',  display_lev: 50, engine_lev: 50 },
] as const

const CELL_SPEC_BY_CELL: Map<CellNonSpectator, CellSpec> = new Map(
  CELL_SPECS.map((s) => [s.cell, s]),
)

export function getCellSpec(cell: CellNonSpectator): CellSpec {
  const spec = CELL_SPEC_BY_CELL.get(cell)
  if (!spec) throw new Error(`unknown cell: ${cell as string}`)
  return spec
}

export const ALL_CELLS: ReadonlyArray<Cell> = [
  'short_x50', 'short_x25', 'short_x10',
  'spectator',
  'long_x10', 'long_x25', 'long_x50',
] as const

export function isCell(v: unknown): v is Cell {
  return typeof v === 'string' && (ALL_CELLS as ReadonlyArray<string>).includes(v)
}

export function isCellNonSpectator(v: unknown): v is CellNonSpectator {
  return isCell(v) && v !== 'spectator'
}

// PLAN.md §6.7 — WebSocket close codes
export const WS_CODES = {
  HANDLE_REQUIRED: 4001,
  AUTH_INVALID: 4002,
  REPLACED: 4003,
  D1_UNAVAILABLE: 4500,
} as const
